import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  Plus, KeyRound, Trash2, Pencil, ShieldCheck, Copy,
} from 'lucide-react';
import { formatApiError, ROLE_LABELS, PERMISSION_GROUPS, getStoredUser } from '@/lib/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const roleBadgeColor = {
  super_admin: 'bg-purple-100 text-purple-800',
  admin: 'bg-blue-100 text-blue-800',
  supervisor: 'bg-emerald-100 text-emerald-800',
  operator: 'bg-slate-100 text-slate-800',
};

export default function UserManagement() {
  const currentUser = getStoredUser();
  const [users, setUsers] = useState([]);
  const [rbac, setRbac] = useState({ role_presets: {}, permissions: [], roles: [] });
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [tempPasswordShown, setTempPasswordShown] = useState(null); // {email, password}

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [uRes, rRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/rbac/permissions`),
      ]);
      setUsers(uRes.data);
      setRbac(rRes.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Delete user "${u.name}" (${u.email})? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/users/${u.id}`);
      toast.success('User deleted');
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleResetPassword = async (u) => {
    if (!window.confirm(`Reset password for ${u.email}? They will be given a temporary password.`)) return;
    try {
      const res = await axios.post(`${API}/users/${u.id}/reset-password`);
      setTempPasswordShown({ email: u.email, password: res.data.temp_password });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="user-management-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
            <ShieldCheck className="h-8 w-8 text-[#1680e5]" />
            Users & Permissions
          </h1>
          <p className="text-slate-600 mt-2">
            Manage user accounts and fine-grained permissions per role.
          </p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#1680e5] hover:bg-[#1680e5]/90" data-testid="create-user-button">
              <Plus className="h-4 w-4 mr-2" />Add user
            </Button>
          </DialogTrigger>
          <UserFormDialog
            mode="create"
            rbac={rbac}
            currentUser={currentUser}
            onDone={(created) => {
              setCreateOpen(false);
              if (created?.temp_password) {
                setTempPasswordShown({ email: created.user.email, password: created.temp_password });
              }
              load();
            }}
          />
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Team ({users.length})</CardTitle>
          <CardDescription>Only super admins and admins can manage users.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-12 text-center text-slate-500">Loading…</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Permissions</TableHead>
                    <TableHead>Last login</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                      <TableCell className="font-medium">{u.name}</TableCell>
                      <TableCell className="text-slate-600">{u.email}</TableCell>
                      <TableCell>
                        <Badge className={roleBadgeColor[u.role] || 'bg-slate-100 text-slate-800'}>
                          {ROLE_LABELS[u.role] || u.role}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {u.active ? (
                          <Badge className="bg-green-100 text-green-800">Active</Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-800">Deactivated</Badge>
                        )}
                        {u.must_change_password && (
                          <Badge className="ml-1 bg-amber-100 text-amber-800">Must reset</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-600 text-sm">{u.permissions?.length ?? 0}</TableCell>
                      <TableCell className="text-slate-500 text-sm">
                        {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : '—'}
                      </TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button
                          variant="ghost" size="icon"
                          onClick={() => setEditUser(u)}
                          data-testid={`edit-user-${u.email}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost" size="icon"
                          onClick={() => handleResetPassword(u)}
                          data-testid={`reset-password-${u.email}`}
                          title="Reset password"
                        >
                          <KeyRound className="h-4 w-4 text-amber-600" />
                        </Button>
                        {currentUser?.id !== u.id && (
                          <Button
                            variant="ghost" size="icon"
                            onClick={() => handleDelete(u)}
                            data-testid={`delete-user-${u.email}`}
                          >
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit dialog */}
      <Dialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)}>
        {editUser && (
          <UserFormDialog
            mode="edit"
            user={editUser}
            rbac={rbac}
            currentUser={currentUser}
            onDone={() => { setEditUser(null); load(); }}
          />
        )}
      </Dialog>

      {/* Temp password shown */}
      <Dialog open={!!tempPasswordShown} onOpenChange={(o) => !o && setTempPasswordShown(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Temporary password created</DialogTitle>
            <DialogDescription>
              Share this password with the user securely. They will be forced to change it on first login.
            </DialogDescription>
          </DialogHeader>
          {tempPasswordShown && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Email</Label>
                <div className="font-mono text-sm bg-slate-100 px-3 py-2 rounded">{tempPasswordShown.email}</div>
              </div>
              <div>
                <Label className="text-xs">Temporary password</Label>
                <div className="flex gap-2 items-center">
                  <code className="font-mono flex-1 bg-slate-100 px-3 py-2 rounded text-sm">
                    {tempPasswordShown.password}
                  </code>
                  <Button
                    variant="outline" size="icon"
                    onClick={() => {
                      navigator.clipboard.writeText(tempPasswordShown.password);
                      toast.success('Copied');
                    }}
                    data-testid="copy-temp-password"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setTempPasswordShown(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function UserFormDialog({ mode, user, rbac, currentUser, onDone }) {
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [role, setRole] = useState(user?.role || 'operator');
  const [permissions, setPermissions] = useState(user?.permissions || rbac.role_presets?.operator || []);
  const [active, setActive] = useState(user?.active !== false);
  const [tempPassword, setTempPassword] = useState('');
  const [sendInvite, setSendInvite] = useState(true);
  const [saving, setSaving] = useState(false);

  const applyRolePreset = (r) => {
    setRole(r);
    if (rbac.role_presets?.[r]) {
      setPermissions([...rbac.role_presets[r]]);
    }
  };

  const togglePerm = (perm) => {
    setPermissions((prev) =>
      prev.includes(perm) ? prev.filter((p) => p !== perm) : [...prev, perm]
    );
  };

  const roleOptions = (rbac.roles || []).filter((r) => {
    // Only super_admin can create/edit super_admins
    if (r === 'super_admin' && currentUser?.role !== 'super_admin') return false;
    return true;
  });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (mode === 'create') {
        const payload = { email, name, role, permissions, send_invite_email: sendInvite };
        if (tempPassword) payload.temp_password = tempPassword;
        const res = await axios.post(`${API}/users`, payload);
        toast.success(res.data.invite_sent ? 'User created — invite email sent' : 'User created');
        onDone(res.data);
      } else {
        await axios.patch(`${API}/users/${user.id}`, {
          name, role, permissions, active,
        });
        toast.success('User updated');
        onDone();
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>{mode === 'create' ? 'Add user' : `Edit ${user?.name}`}</DialogTitle>
        <DialogDescription>
          Choose a role to load default permissions, then fine-tune as needed.
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="user-name">Full name</Label>
            <Input id="user-name" required value={name} onChange={(e) => setName(e.target.value)} data-testid="user-form-name" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="user-email">Email</Label>
            <Input
              id="user-email" type="email" required
              value={email} onChange={(e) => setEmail(e.target.value)}
              disabled={mode === 'edit'}
              data-testid="user-form-email"
            />
          </div>
          <div className="space-y-2">
            <Label>Role</Label>
            <Select value={role} onValueChange={applyRolePreset}>
              <SelectTrigger data-testid="user-form-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {roleOptions.map((r) => (
                  <SelectItem key={r} value={r}>{ROLE_LABELS[r] || r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {mode === 'create' ? (
            <div className="space-y-2">
              <Label htmlFor="user-temp-password">Temporary password (optional)</Label>
              <Input
                id="user-temp-password" type="text"
                placeholder="Leave blank to auto-generate"
                value={tempPassword} onChange={(e) => setTempPassword(e.target.value)}
                data-testid="user-form-temp-password"
              />
            </div>
          ) : (
            <div className="flex items-center gap-3 pt-6">
              <Switch
                checked={active} onCheckedChange={setActive}
                id="user-active" data-testid="user-form-active"
              />
              <Label htmlFor="user-active">{active ? 'Active' : 'Deactivated'}</Label>
            </div>
          )}
        </div>

        {mode === 'create' && (
          <label className="flex items-start gap-2 text-sm cursor-pointer border rounded-md p-3 bg-slate-50">
            <Checkbox
              checked={sendInvite}
              onCheckedChange={setSendInvite}
              data-testid="user-form-send-invite"
              className="mt-0.5"
            />
            <span>
              <span className="font-medium text-slate-800">Send invitation email</span>
              <span className="block text-xs text-slate-500 mt-0.5">
                Emails the user their login link + temporary password so you don&rsquo;t have to share it manually.
              </span>
            </span>
          </label>
        )}

        <div>
          <div className="flex items-center justify-between mb-2">
            <Label>Permissions ({permissions.length})</Label>
            <Button
              type="button" variant="ghost" size="sm"
              onClick={() => applyRolePreset(role)}
              data-testid="reset-permissions-preset"
            >
              Reset to {ROLE_LABELS[role]} defaults
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border rounded-lg p-4 bg-slate-50">
            {PERMISSION_GROUPS.map((group) => (
              <div key={group.label} className="space-y-2">
                <div className="text-sm font-semibold text-slate-700">{group.label}</div>
                {group.items.map((it) => (
                  <label
                    key={it.key}
                    className="flex items-start gap-2 text-sm cursor-pointer"
                    data-testid={`permission-toggle-${it.key}`}
                  >
                    <Checkbox
                      checked={permissions.includes(it.key)}
                      onCheckedChange={() => togglePerm(it.key)}
                    />
                    <span className="text-slate-700">{it.label}</span>
                  </label>
                ))}
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button type="submit" disabled={saving} className="bg-[#1680e5] hover:bg-[#1680e5]/90" data-testid="user-form-submit">
            {saving ? 'Saving…' : mode === 'create' ? 'Create user' : 'Save changes'}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
